import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { Plus, Search, SlidersHorizontal, ArrowUp, Monitor, Smartphone } from 'lucide-react';
import { DottedLogo } from '../components/DottedLogo';
import { StartingPointSelector } from '../components/StartingPointSelector';
import { UseCaseCrossLinks } from '../components/UseCaseCrossLinks';
import { UseCaseTestimonials } from '../components/UseCaseTestimonials';
import { ClosingCTA } from '../components/ClosingCTA';
const TESTIMONIALS = [{
  name: 'Sarah Chen',
  role: 'CEO & Founder',
  avatar: 'https://i.pravatar.cc/100?img=47',
  quote: 'I used to spend Sunday nights digging through Slack and email to prep for Monday. Now Giga drops a briefing doc in my lap at 8 AM.'
}, {
  name: 'Marcus Weaver',
  role: '2x Founder',
  avatar: 'https://i.pravatar.cc/100?img=11',
  quote: 'The open loop sweep is a lifesaver. It caught an investor request I completely missed in a WhatsApp thread.'
}, {
  name: 'Elena Rostova',
  role: 'Chief of Staff',
  avatar: 'https://i.pravatar.cc/100?img=32',
  quote: "It's like having a co-pilot who has read every email, CRM note, and Slack message we've ever received."
}];
const ROTATING_CLAIMS = ['Runs your morning briefing', 'Triages your inbox', 'Books your travel', 'Reminds you to follow-up', 'Reschedules your week', 'Researches your competitors', 'Runs your errands', 'Preps you for interviews'];
const FEATURED_APPS = [{
  slug: 'hubspot',
  label: 'HubSpot'
}, {
  slug: 'salesforce',
  label: 'Salesforce'
}, {
  slug: 'slack',
  label: 'Slack'
}, {
  slug: 'gmail',
  label: 'Gmail'
}, {
  slug: 'google-calendar',
  label: 'Calendar'
}, {
  slug: 'whatsapp',
  label: 'WhatsApp'
}, {
  slug: 'amazon',
  label: 'Amazon'
}];
type GalleryCard = {
  id: string;
  kind: string;
  title: string;
  quote: string;
  gradient: string;
  icon: string;
  ui: React.ReactNode;
};
// Cards intentionally alternate Business → Personal → Business → … so the
// marquee oscillates between the two worlds as it scrolls.
const GALLERY_CARDS: GalleryCard[] = [{
  id: 'prep-sales-call',
  kind: 'Business',
  title: 'Sales calls, prepped',
  quote: '"Prep me for my Acme call tomorrow. Fetch the HubSpot deal, search Gmail for recent threads, and summarize the proposal."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'hubspot',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-3581">
        <div className="h-2 w-16 bg-neutral-200 rounded mb-3" data-id="element-3582" />
        <div className="flex justify-between items-center mb-2" data-id="element-3583">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-3584">
            Acme Corp
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-3585">$84k ARR</span>
        </div>
        <div className="h-1.5 w-full bg-neutral-100 rounded mb-1.5" data-id="element-3586" />
        <div className="h-1.5 w-3/4 bg-neutral-100 rounded" data-id="element-3587" />
      </div>
}, {
  id: 'travel-booked',
  kind: 'Personal',
  title: 'Travel, booked',
  quote: '"Book my Friday trip to SF. Find a window seat under $400, a hotel near the office, and add it all to my calendar."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'google-calendar',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-3588">
        <div className="flex items-center justify-between mb-3" data-id="element-3589">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-3590">
            SFO · Fri
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-3591">$382</span>
        </div>
        <div className="flex items-center gap-2 mb-2" data-id="element-3592">
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-3593" />
          <div className="flex-1 h-px bg-neutral-200" data-id="element-3594" />
          <div className="h-1.5 w-6 bg-neutral-300 rounded" data-id="element-3595" />
        </div>
        <div className="h-1.5 w-2/3 bg-neutral-100 rounded" data-id="element-3596" />
      </div>
}, {
  id: 'board-decks',
  kind: 'Business',
  title: 'Board decks, updated',
  quote: '"Update the board deck with the latest metrics. Pull MRR from Stripe, burn from QuickBooks, and pipeline from Salesforce."',
  gradient: 'from-[#FBF1E7] to-[#FBDFC4]',
  icon: 'stripe',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-3597">
        <div className="flex justify-between items-center mb-3" data-id="element-3598">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-3599">
            MRR Growth
          </span>
          <span className="text-[10px] font-bold text-green-600" data-id="element-3600">+18%</span>
        </div>
        <div className="h-16 w-full flex items-end gap-1" data-id="element-3601">
          {[40, 55, 45, 70, 65, 85, 100].map((h, i) => <div key={i} className="flex-1 bg-[#EA580C]/25 rounded-t-sm" style={{
        height: `${h}%`
      }} data-id="element-3602" />)}
        </div>
      </div>
}, {
  id: 'errands-handled',
  kind: 'Personal',
  title: 'Errands, handled',
  quote: '"Reorder my usual groceries, renew my car registration, and reschedule the dentist for next week. Confirm when each is done."',
  gradient: 'from-[#FBF1E7] to-[#FCE2CC]',
  icon: 'amazon',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-3603">
        <div className="h-2 w-20 bg-neutral-800 rounded mb-4" data-id="element-3604" />
        <div className="space-y-2.5" data-id="element-3605">
          {[true, true, false].map((done, i) => <div key={i} className="flex items-center gap-2" data-id="element-3606">
              <div className={`w-3 h-3 rounded-sm shrink-0 ${done ? 'bg-[#EA580C]' : 'border border-neutral-300'}`} data-id="element-3607" />
              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-3608" />
            </div>)}
        </div>
      </div>
}, {
  id: 'inbox-triaged',
  kind: 'Business',
  title: 'Inbox, triaged',
  quote: '"Clear my inbox. Reply to anything routine, flag what needs me, and turn the rest into tasks so nothing slips."',
  gradient: 'from-[#FBF4EC] to-[#F4E7D6]',
  icon: 'gmail',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-3609">
        <div className="flex items-center justify-between mb-3" data-id="element-3610">
          <span className="text-[10px] font-bold text-neutral-800" data-id="element-3611">Inbox</span>
          <span className="text-[10px] font-bold text-[#EA580C]" data-id="element-3612">3 left</span>
        </div>
        <div className="space-y-2" data-id="element-3613">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-3614">
              <div className="w-2 h-2 rounded-full bg-[#EA580C]/40 shrink-0" data-id="element-3615" />
              <div className="h-1.5 flex-1 bg-neutral-100 rounded" data-id="element-3616" />
            </div>)}
        </div>
      </div>
}, {
  id: 'track-open-loops',
  kind: 'Personal',
  title: 'Open loops, closed',
  quote: '"Show me every open loop across work and life. Check my work email, personal email, Slack, and iMessage for anything I missed."',
  gradient: 'from-[#FBF4EC] to-[#F3E9DB]',
  icon: 'slack',
  ui: <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4 shadow-sm w-48 border border-white/40" data-id="element-3617">
        <div className="h-2 w-24 bg-neutral-800 rounded mb-4" data-id="element-3618" />
        <div className="space-y-2.5" data-id="element-3619">
          {[1, 2, 3].map(i => <div key={i} className="flex items-center gap-2" data-id="element-3620">
              <div className="w-3 h-3 rounded-sm border border-neutral-300 shrink-0" data-id="element-3621" />
              <div className="h-1.5 w-full bg-neutral-200 rounded" data-id="element-3622" />
            </div>)}
        </div>
      </div>
}];
function GeneratingStep({
  icon,
  text,
  index
}: {
  icon: string;
  text: string;
  index: number;
}) {
  const reduce = useReducedMotion();
  const words = text.split(' ');
  return <div className="flex items-start gap-3" data-id="element-3623">
      <div className="relative w-6 h-6 shrink-0 mt-0.5" data-id="element-3624">
        <motion.div initial={{
        scale: 0.5,
        opacity: 0
      }} whileInView={{
        scale: 1,
        opacity: 1
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        type: 'spring',
        stiffness: 300,
        damping: 20,
        delay: reduce ? 0 : index * 0.25
      }} className="absolute inset-0 rounded bg-white/10 flex items-center justify-center z-10" data-id="element-3625">
          <AppIcon slug={icon} className="w-3.5 h-3.5 object-contain" data-id="element-3626" />
        </motion.div>
        {!reduce && <motion.div initial={{
        opacity: 0,
        scale: 0.8
      }} whileInView={{
        opacity: [0, 1, 0],
        scale: [0.8, 1.4, 1.6]
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.8,
        delay: index * 0.25,
        ease: 'easeOut'
      }} className="absolute inset-0 rounded border border-[#EA580C]/40" data-id="element-3627" />}
      </div>

      <p className="text-sm text-neutral-300 leading-relaxed flex flex-wrap gap-x-[0.25em]" data-id="element-3628">
        {words.map((word, wIdx) => <motion.span key={wIdx} initial={{
        opacity: 0,
        y: 4
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-50px'
      }} transition={{
        duration: 0.2,
        delay: reduce ? 0 : index * 0.25 + 0.1 + wIdx * 0.02
      }} className="inline-block" data-id="element-3629">
            {word}
          </motion.span>)}
      </p>
    </div>;
}
function AppIcon({
  slug,
  className
}: {
  slug: string;
  className?: string;
}) {
  return <img src={`https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/svg/${slug}.svg`} alt="" aria-hidden="true" className={className} onError={e => {
    const img = e.currentTarget as HTMLImageElement;
    if (!img.src.includes('simpleicons')) {
      img.src = `https://cdn.simpleicons.org/${slug.replace(/-/g, '')}`;
    } else {
      img.style.visibility = 'hidden';
    }
  }} data-id="element-3630" />;
}
function GalleryJobCard({
  card
}: {
  card: GalleryCard;
}) {
  const isBusiness = card.kind === 'Business';
  return <div className="w-[280px] md:w-[320px] shrink-0 flex flex-col gap-6" data-id="element-3631">
      {/* Visual Area */}
      <div className={`h-[340px] rounded-3xl bg-gradient-to-br ${card.gradient} p-6 relative overflow-hidden flex flex-col items-center justify-center`} data-id="element-3632">
        <div className="relative z-10 flex flex-col items-center" data-id="element-3633">
          {card.ui}
          <div className="w-px h-12 bg-black/10 my-2" data-id="element-3634" />
          <div className="w-10 h-10 rounded-full bg-white/80 backdrop-blur-md shadow-sm border border-white flex items-center justify-center" data-id="element-3635">
            <AppIcon slug={card.icon} className="w-5 h-5 object-contain" data-id="element-3636" />
          </div>
        </div>
        <div className="absolute inset-0 opacity-[0.03] mix-blend-overlay pointer-events-none" style={{
        backgroundImage: 'url("https://grainy-gradients.vercel.app/noise.svg")'
      }} data-id="element-3637" />
      </div>

      {/* Text Area */}
      <div className="flex flex-col gap-3 px-2" data-id="element-3638">
        <span className="flex items-center gap-2 font-mono text-[10px] tracking-[0.22em] uppercase" data-id="element-3639">
          <span className={`w-1.5 h-1.5 rounded-full ${isBusiness ? 'bg-[#EA580C]' : 'bg-[#2B211A]'}`} data-id="element-3640" />
          <span className={isBusiness ? 'text-[#EA580C]' : 'text-neutral-500'} data-id="element-3641">
            {card.kind}
          </span>
        </span>
        <h3 className="text-xl font-bold text-[#2B211A] -mt-1" data-id="element-3642">{card.title}</h3>
        <p className="text-sm text-neutral-600 leading-relaxed" data-id="element-3643">{card.quote}</p>
      </div>
    </div>;
}
export function FoundersExecs() {
  const [claimIndex, setClaimIndex] = useState(0);
  const reduce = useReducedMotion();
  useEffect(() => {
    if (reduce) return;
    const id = setInterval(() => {
      setClaimIndex(i => (i + 1) % ROTATING_CLAIMS.length);
    }, 2600);
    return () => clearInterval(id);
  }, [reduce]);
  return <>
      <section className="relative w-full bg-[#FAF8F4] text-[#2B211A] overflow-hidden" data-id="element-3644">
        {/* Cream dot-grid texture */}
        <div aria-hidden="true" className="cream-grid z-0" data-id="element-3645" />

        <div className="relative z-10 max-w-[1200px] mx-auto px-6 pt-32 md:pt-40 pb-20 md:pb-28" data-id="element-3646">
          {/* Eyebrow */}
          <div className="text-center" data-id="element-3647">
            <span className="inline-block text-xs font-semibold tracking-[0.22em] uppercase text-neutral-400 mb-5" data-id="element-3648">
              Founders & Execs
            </span>

            {/* Headline */}
            <h1 className="font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#2B211A]" data-id="element-3649">
              Your new assistant that
            </h1>

            {/* Rotating claim */}
            <div className="mt-4 h-[1.2em] font-title font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-[#EA580C]" data-id="element-3650">
              <AnimatePresence mode="wait" data-id="element-3651">
                <motion.span key={claimIndex} initial={{
                opacity: 0,
                y: 14
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -14
              }} transition={{
                duration: 0.4,
                ease: [0.22, 1, 0.36, 1]
              }} className="block" data-id="element-3652">
                  {ROTATING_CLAIMS[claimIndex]}
                </motion.span>
              </AnimatePresence>
            </div>

            {/* CTA */}
            <div className="mt-12 flex flex-col items-center gap-4" data-id="element-3653">
              <Link to="/signup" className="bg-[#2B211A] hover:bg-[#1A1410] text-white px-10 py-4 rounded-full font-bold text-lg transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-0.5" data-id="element-3654">
                Get Started
              </Link>
              <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-neutral-400" data-id="element-3655">
                Each Giga plan has a 7 day free trial.
              </p>
            </div>

            {/* Featured apps */}
            <div className="mt-16 pt-10 border-t border-[#EAE4D9] max-w-5xl mx-auto" data-id="element-3656">
              <p className="font-mono text-[10px] tracking-[0.28em] uppercase text-neutral-400 mb-6" data-id="element-3657">
                Featured Apps
              </p>
              <div className="flex flex-wrap md:flex-nowrap justify-center gap-3 md:gap-4" data-id="element-3658">
                {FEATURED_APPS.map(app => <div key={app.label} className="flex items-center gap-2.5 rounded-xl bg-white border border-[#EAE4D9] px-5 py-2.5 shadow-sm hover:shadow-md transition-shadow" data-id="element-3659">
                    <AppIcon slug={app.slug} className="w-5 h-5 object-contain grayscale opacity-70" data-id="element-3660" />
                    <span className="text-sm font-bold text-[#2B211A]" data-id="element-3661">
                      {app.label}
                    </span>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Put Giga to work — job gallery */}
          <div className="mt-24 md:mt-32 w-full overflow-hidden" data-id="element-3662">
            <div className="text-center mb-12 max-w-2xl mx-auto" data-id="element-3663">
              <span className="inline-block text-xs font-semibold tracking-[0.22em] uppercase text-neutral-400 mb-4" data-id="element-3664">
                Put Giga to work
              </span>
              <h2 className="font-title font-bold text-3xl md:text-4xl text-[#2B211A]" data-id="element-3665">
                Give Giga a job.
              </h2>
              <p className="mt-4 text-base md:text-lg text-neutral-500 leading-relaxed" data-id="element-3666">
                One assistant for both worlds — it moves between your business
                and your personal life without missing a beat.
              </p>
            </div>

            {/* Auto-scrolling marquee, oscillating Business ↔ Personal.
             Falls back to a manual horizontal scroll for reduced motion. */}
            {reduce ? <div className="flex overflow-x-auto gap-6 pb-4 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-3667">
                {GALLERY_CARDS.map(card => <GalleryJobCard key={card.id} card={card} data-id="element-3668" />)}
              </div> : <div className="relative w-full overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_6%,black_94%,transparent)]" data-id="element-3669">
                <motion.div className="flex gap-6 w-max" animate={{
              x: ['-50%', '0%']
            }} transition={{
              duration: 48,
              ease: 'linear',
              repeat: Infinity
            }} data-id="element-3670">
                  {[...GALLERY_CARDS, ...GALLERY_CARDS].map((card, i) => <GalleryJobCard key={`${card.id}-${i}`} card={card} data-id="element-3671" />)}
                </motion.div>
              </div>}
          </div>

          {/* Giga Does the Work comparison section */}
          <div className="mt-32 md:mt-40" data-id="element-3672">
            <div className="text-center mb-20" data-id="element-3673">
              <span className="inline-block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-4" data-id="element-3674">
                Giga does the work
              </span>
              <h2 className="font-title font-bold text-4xl md:text-5xl text-[#2B211A]" data-id="element-3675">
                Other tools advise.{' '}
                <span className="text-[#EA580C]" data-id="element-3676">Giga executes.</span>
              </h2>
            </div>

            <div className="space-y-24" data-id="element-3677">
              {/* Row 1: MEETING PREP */}
              <div className="flex flex-col gap-8" data-id="element-3678">
                <div className="text-center max-w-3xl mx-auto" data-id="element-3679">
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EA580C] mb-3 block" data-id="element-3680">
                    Meeting Prep
                  </span>
                  <h3 className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-3681">
                    Prep me for my Acme call tomorrow
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-3682">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-3683">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-3684">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-3685">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-3686">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-3687">
                      "Log into HubSpot, read the notes. Log into Gmail, search
                      the thread. Open Google Drive, find the proposal. Spend 30
                      minutes piecing it together..."
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-3688">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-3689" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-3690">
                      <div className="flex items-center gap-2" data-id="element-3691">
                        <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-3692" />
                        <img src="https://cdn.magicpatterns.com/uploads/fUuKba8qU1ywiSyZ4Pf9Xu/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-3693" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-3694">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-3695" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-3696" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-3697">
                      {[{
                      icon: 'hubspot',
                      text: 'Fetching the Acme deal record and stage'
                    }, {
                      icon: 'gmail',
                      text: 'Searching recent threads for context'
                    }, {
                      icon: 'google-drive',
                      text: 'Finding the latest proposal draft'
                    }, {
                      icon: 'slack',
                      text: 'Drafting the briefing doc for the team'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-3698" />)}
                    </div>
                    <motion.div initial={reduce ? {
                    opacity: 1,
                    y: 0
                  } : {
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true,
                    margin: '-50px'
                  }} transition={{
                    duration: 0.5,
                    delay: reduce ? 0 : 1.4
                  }} className="relative z-10 bg-[#EA580C] rounded-xl p-3 md:p-6 mt-auto shadow-xl shadow-orange-900/30" data-id="element-3699">
                      <span className="block text-[9px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-1.5 md:mb-2" data-id="element-3700">
                        The payoff
                      </span>
                      <p className="text-sm md:text-xl font-bold text-white leading-snug" data-id="element-3701">
                        You walk into the meeting with the exact context you
                        need, zero digging required.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>

              {/* Row 2: OPEN LOOPS */}
              <div className="flex flex-col gap-8" data-id="element-3702">
                <div className="text-center max-w-3xl mx-auto" data-id="element-3703">
                  <span className="font-mono text-[10px] tracking-[0.28em] uppercase text-[#EA580C] mb-3 block" data-id="element-3704">
                    Open Loops
                  </span>
                  <h3 className="font-title text-2xl md:text-3xl font-bold text-[#2B211A] leading-tight" data-id="element-3705">
                    What am I forgetting across work and life?
                  </h3>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10" data-id="element-3706">
                  {/* Generic tool card */}
                  <div className="rounded-2xl bg-white border border-[#EAE4D9] shadow-sm p-6 md:p-8 flex flex-col" data-id="element-3707">
                    <div className="flex items-center gap-3 mb-6 pb-4 border-b border-[#EAE4D9]" data-id="element-3708">
                      <div className="w-6 h-6 rounded bg-neutral-100 flex items-center justify-center text-xs font-bold text-neutral-400" data-id="element-3709">
                        ?
                      </div>
                      <span className="text-sm font-bold text-neutral-400 uppercase tracking-wider" data-id="element-3710">
                        Generic AI
                      </span>
                    </div>
                    <p className="text-base text-neutral-500 italic leading-relaxed font-serif" data-id="element-3711">
                      "You can't. You just have to remember everything, or spend
                      2 hours manually sweeping 4 inboxes and 3 chat apps."
                    </p>
                  </div>

                  {/* Giga card (Dark Theme) */}
                  <div className="rounded-2xl bg-[#2B211A] shadow-2xl shadow-orange-900/10 p-6 md:p-8 flex flex-col relative overflow-hidden" data-id="element-3712">
                    <DottedLogo variant="dark" opacity={0.1} className="inset-0 scale-150" data-id="element-3713" />
                    <div className="relative z-10 flex items-center gap-3 mb-6 pb-4 border-b border-white/10" data-id="element-3714">
                      <div className="flex items-center gap-2" data-id="element-3715">
                        <img src="https://cdn.magicpatterns.com/uploads/bxQBDjkPJfWx4r7DHyApcd/Vector.png" alt="" aria-hidden="true" className="w-5 h-5 object-contain [filter:brightness(0)_saturate(100%)_invert(60%)_sepia(60%)_saturate(2500%)_hue-rotate(346deg)_brightness(100%)_contrast(96%)]" data-id="element-3716" />
                        <img src="https://cdn.magicpatterns.com/uploads/fUuKba8qU1ywiSyZ4Pf9Xu/giga4x.png" alt="Giga" className="h-3.5 object-contain [filter:brightness(0)_invert(1)]" data-id="element-3717" />
                      </div>
                      <span className="ml-auto flex h-2.5 w-2.5 relative" data-id="element-3718">
                        <span className="absolute inline-flex h-full w-full rounded-full bg-[#EA580C] opacity-75 animate-ping" data-id="element-3719" />
                        <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#EA580C]" data-id="element-3720" />
                      </span>
                    </div>
                    <div className="relative z-10 space-y-4 mb-8 flex-1" data-id="element-3721">
                      {[{
                      icon: 'gmail',
                      text: 'Scanning Work Email for promised follow-ups'
                    }, {
                      icon: 'google-calendar',
                      text: 'Checking Personal Calendar for family obligations'
                    }, {
                      icon: 'slack',
                      text: 'Searching Slack DMs and exec channels'
                    }, {
                      icon: 'apple',
                      text: 'Finding loose texts and quick asks'
                    }].map((step, i) => <GeneratingStep key={i} icon={step.icon} text={step.text} index={i} data-id="element-3722" />)}
                    </div>
                    <motion.div initial={reduce ? {
                    opacity: 1,
                    y: 0
                  } : {
                    opacity: 0,
                    y: 10
                  }} whileInView={{
                    opacity: 1,
                    y: 0
                  }} viewport={{
                    once: true,
                    margin: '-50px'
                  }} transition={{
                    duration: 0.5,
                    delay: reduce ? 0 : 1.4
                  }} className="relative z-10 bg-[#EA580C] rounded-xl p-3 md:p-6 mt-auto shadow-xl shadow-orange-900/30" data-id="element-3723">
                      <span className="block text-[9px] md:text-[11px] font-bold uppercase tracking-[0.2em] text-white/80 mb-1.5 md:mb-2" data-id="element-3724">
                        The payoff
                      </span>
                      <p className="text-sm md:text-xl font-bold text-white leading-snug" data-id="element-3725">
                        You never drop a ball again. Every open loop, surfaced
                        and prioritized.
                      </p>
                    </motion.div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pick the function that eats the most time, then see Giga handle it */}
      <StartingPointSelector data-id="element-3726" />

      {/* Role-relevant social proof right before the conversion moment */}
      <UseCaseTestimonials testimonials={TESTIMONIALS} data-id="element-3727" />

      {/* Keep visitors in the funnel: other use cases, then the conversion CTA */}
      <UseCaseCrossLinks current="founders-execs" data-id="element-3728" />
      <ClosingCTA data-id="element-3729" />
    </>;
}